'use client';

import { useRef } from 'react';

import { css } from '@flipster/styled-system/css';
import { circle, hstack } from '@flipster/styled-system/patterns';
import { formatToShortNumber } from '@flipster/utils/num';

const barHeight = 6;
const handleSize = 24;

interface VolumeSliderProps {
  marks: number[];
  value: number;
  onChange: (value: number) => void;
}

export const VolumeSlider = ({ marks, value, onChange }: VolumeSliderProps) => {
  const sliderRef = useRef<HTMLDivElement>(null);

  const sortedMarks = marks.length > 0 ? marks : [0];
  const min = sortedMarks[0];
  const max = sortedMarks[sortedMarks.length - 1];

  function getValueFromPointer(pointerX: number) {
    const rect = sliderRef.current!.getBoundingClientRect();
    const ratio = Math.max(0, Math.min(1, (pointerX - rect.left) / rect.width));
    return markWeightedScale(ratio, sortedMarks);
  }

  function snapToNearestMark(val: number) {
    let closest = sortedMarks[0];
    let minDist = Math.abs(val - closest);
    for (const mark of sortedMarks) {
      const dist = Math.abs(val - mark);
      if (dist < minDist) {
        minDist = dist;
        closest = mark;
      }
    }
    return closest;
  }

  function handlePointerEvent(pointerX: number) {
    const raw = getValueFromPointer(pointerX);
    onChange(snapToNearestMark(raw));
  }

  const percent = getMarkWeightedPercent(sortedMarks, value);

  return (
    <div className={css({ w: '100%' })}>
      <div
        ref={sliderRef}
        className={css({
          pos: 'relative',
          w: '100%',
          h: handleSize + barHeight,
          py: (handleSize - barHeight) / 2,
          cursor: 'pointer',
          touchAction: 'none',
        })}
        onPointerDown={(e) => {
          (e.target as HTMLElement).setPointerCapture(e.pointerId);
          e.preventDefault();
          handlePointerEvent(e.clientX);
        }}
        onPointerMove={(e) => {
          if (!(e.target as HTMLElement).hasPointerCapture(e.pointerId)) return;
          handlePointerEvent(e.clientX);
        }}
        onPointerUp={(e) => {
          if ((e.target as HTMLElement).hasPointerCapture(e.pointerId)) {
            (e.target as HTMLElement).releasePointerCapture(e.pointerId);
          }
        }}
      >
        {/* Track background */}
        <div
          className={css({
            h: barHeight,
            bgColor: 'border.default.level-3',
            rounded: barHeight,
          })}
        />
        {/* Track fill */}
        <span
          className={css({
            pos: 'absolute',
            top: (handleSize - barHeight) / 2,
            left: 0,
            h: barHeight,
            rounded: barHeight,
            bg: 'rgba(255, 208, 132)',
          })}
          style={{ width: `${percent}%` }}
        />
        {/* Handle */}
        <span
          className={circle({
            pos: 'absolute',
            size: handleSize,
            bg: 'rgba(255, 208, 132)',
            top: 0,
            transform: 'translateX(-50%)',
            zIndex: 1,
            cursor: 'grab',
            _active: { cursor: 'grabbing' },
          })}
          style={{ left: `${percent}%` }}
        />
      </div>
      {/* Labels */}
      <div
        className={hstack({
          justifyContent: 'space-between',
          textStyle: 'Text/S/Regular',
          color: 'content.default.level-2',
        })}
      >
        <span>{formatToShortNumber(min.toString())} USDT</span>
        <span>{formatToShortNumber(max.toString())}+ USDT</span>
      </div>
    </div>
  );
};

function markWeightedScale(ratio: number, marks: number[]): number {
  if (marks.length <= 1) return marks[0] ?? 0;
  const segment = ratio * (marks.length - 1);
  const leftIdx = Math.min(Math.floor(segment), marks.length - 2);
  const left = marks[leftIdx];
  const right = marks[leftIdx + 1];
  const ratioInSegment = segment - leftIdx;
  return left + ratioInSegment * (right - left);
}

function getMarkWeightedPercent(marks: number[], value: number): number {
  if (marks.length <= 1) return 0;
  const idx = marks.findIndex((m) => m >= value);
  if (idx <= 0) return idx === 0 ? 0 : 100;
  const left = marks[idx - 1];
  const right = marks[idx];
  const segmentRatio = right === left ? 0 : (value - left) / (right - left);
  const unit = 1 / (marks.length - 1);
  return ((idx - 1) * unit + segmentRatio * unit) * 100;
}
